/**
 * Creates an object including all relevant data for a product
 * Also allows for updates when a new option is chosen
 * @module lib/createProductData
 */

import $ from 'jquery';
import { PRICE, REGULAR_PRICE, PRODUCT_BLOCK, PRICE_FORMATTED, REGULAR_PRICE_FORMATTED,
  SALE_DISCOUNT_PRICE_FORMATTED } from '../lib/constants/pdp';

const PRICE_ATTR = `data-${PRICE}`;
const REGULAR_PRICE_ATTR = `data-${REGULAR_PRICE}`;
const PRICE_FORMATTED_ATTR = `data-${PRICE_FORMATTED}`;
const REGULAR_PRICE_FORMATTED_ATTR = `data-${REGULAR_PRICE_FORMATTED}`;

/**
 * @namespace
 * @property {String} wrapBlockClass - Class of wrap HTML Element
 */
const DEFAULT_OPTIONS = {
  wrapBlockClass: PRODUCT_BLOCK,
};

/**
 * Helper function to determine if an option has an attr
 * @private
 * @param {HTMLElement} option - The select option element
 * @param {String} attr - The attribute we're checking for
 */
const hasAttr = (option, attr) =>
  !!(option.getAttribute(attr) && option.getAttribute(attr) !== 'false');

/**
 * Helper function to determine if any options have an attr
 * @private
 * @param {NodeList} options - List of select options
 * @param {Function} comparison - Callback function to evaluate the comparison
 * @param {String} attr - The attribute we're comparing
 * @return {Boolean} Whether comparison was successful on any options
 */
const any = (options, comparison, attr) => {
  let len = options.length;

  while (len) {
    len -= 1;
    const option = options[len];
    if (option.value !== 'unavailable'
     && comparison(option, attr)) {
      return true;
    }
  }
  return false;
};

/**
 * Helper function to determine if all options have an attr
 * @private
 * @param {NodeList} options - List of select options
 * @param {Function} comparison - Callback function to evaluate the comparison
 * @param {String} attr - The attribute we're comparing
 * @return {Boolean} Whether comparison was successful on all options
 */
const all = (options, comparison, attr) => {
  let len = options.length;

  while (len) {
    len -= 1;
    const option = options[len];
    if (option.value !== 'unavailable'
     && !comparison(option, attr)) {
      return false;
    }
  }
  return true;
};

/**
 * Helper function to determine if option is on sale
 * @private
 * @param {HTMLElement} option - Option for which we're comparing price
 * @param {String} regularPrice - The item's regular price
 * @return {Boolean} Whether price is less than regualarPrice
 */
const isOnSale = (option, regularPrice) => {
  const price = option.getAttribute(PRICE_ATTR);

  if (!price) {
    // Originally for ColorSwatch for beauty volume options, which only have formatted prices
    // presumably price <= regular price
    const priceFormatted = option.getAttribute(PRICE_FORMATTED_ATTR);
    const regularPriceFormatted = option.getAttribute(REGULAR_PRICE_FORMATTED_ATTR);
    if (priceFormatted && regularPriceFormatted) {
      return priceFormatted !== regularPriceFormatted;
    }
  }
  return parseFloat(price) < parseFloat(regularPrice);
};

/**
 * Helper function to get product data attributes
 * @private
 * @param {HTMLElement} option - The select option element
 * @param {String} attr - The attribute we're checking for
 * @return {String|Boolean} The attribute value (or existence)
 */
const getAttr = (option, attr) => {
  const val = option.getAttribute(`data-${attr}`) || false;
  return (val && val !== 'false') ? (val === 'true') || val || true : false;
};

function getHTMLJSSaveId(value) {
  return value.toString().replace(/[\W_]+/g, '');
}

/**
 * Updates product data object whenever chosen option is changed
 * @param {Object} product - The product's data object
 * @param {HTMLElement} chosen - The chosen option
 */
export const updateChosenData = (product, chosen) => {
  const price = getAttr(chosen, PRICE);
  const regularPrice = getAttr(chosen, REGULAR_PRICE) || price;
  const preorderChosen = getAttr(chosen, 'preorder');
  const oosChosen = getAttr(chosen, 'oos');
  const sizeChosen = chosen.value;
  Object.assign(product, { chosen: {
    el: chosen,
    'delivery-date': getAttr(chosen, 'delivery-date'),
    'defect-disclaimer': getAttr(chosen, 'defect-disclaimer'),
    'final-sale': getAttr(chosen, 'final-sale'),
    'one-left': getAttr(chosen, 'one-left'),
    'removed-item': getAttr(chosen, 'removed-item'),
    preorder: preorderChosen,
    oos: oosChosen,
    unavailable: getAttr(chosen, 'unavailable'),
    price,
    priceFormatted: getAttr(chosen, PRICE_FORMATTED),
    regularPrice,
    regularPriceFormatted: getAttr(chosen, REGULAR_PRICE_FORMATTED),
    saleDiscountPriceFormatted: getAttr(chosen, SALE_DISCOUNT_PRICE_FORMATTED),
    isOnSale: isOnSale(chosen, regularPrice),
    value: chosen.value || '',
    'inventory-disclaimer': getAttr(chosen, 'inventory-disclaimer'),
    sellableDefectItemPrice: getAttr(chosen, 'sellable-defect-item-price'),
    sellableDefectItemLink: getAttr(chosen, 'sellable-defect-item-link'),
    fwrdInStock: getAttr(chosen, 'fwrd'),
  } });
  if (document.getElementById('afterPayBox')) {
    if (preorderChosen || oosChosen) {
      $('#afterPayBox').hide();
    } else {
      $('#afterPayBox').show();
      if (sizeChosen !== '') {
        const afterPayMessageBySize = $(`#afterPayBox${getHTMLJSSaveId(sizeChosen)}`);
        if (afterPayMessageBySize.length) {
          $('.afterPayBoxMsg').hide();
          afterPayMessageBySize.show();
        }
      }
    }
  }
  if (document.getElementById('klarnaPayMsgOption')) {
    if (preorderChosen) {
      $('#klarnaPayMsgOption').hide();
    } else {
      $('#klarnaPayMsgOption').show();
      if (sizeChosen !== '') {
        const klarnaMessageBySize = $(`#installmentPlanMsg${getHTMLJSSaveId(sizeChosen)}`);
        if (klarnaMessageBySize.length) {
          $('.installmentPlanBoxMsg').hide();
          klarnaMessageBySize.show();
        }
      }
    }
  }
};

/**
 * Creates a javascript object containing product data
 * @param {jQuery|Element} sizeEl - The size element (select, input, etc)
 * @see DEFAULT_OPTIONS
 # @return {Object} Contains relevant data to a product
 */
export default function createProductData(sizeEl, opts = {}) {
  const { wrapBlockClass } = Object.assign({}, DEFAULT_OPTIONS, opts);
  const $wrap = $(sizeEl).closest(`.${wrapBlockClass}`);

  // whether input or select, find the chosen element
  const { options, selectedIndex } = sizeEl;
  const chosen = options ? options[selectedIndex] : sizeEl;
  const defaultOption = options ? options[0] : chosen;
  // TODO: deprecate the usage of "regular-price" on the "default" option.
  // Instead, each option should include its own "regular-price".
  // Overall, we should get away from referring to the "default" option.
  const regularPrice = defaultOption.getAttribute(REGULAR_PRICE_ATTR) ||
    defaultOption.getAttribute(PRICE_ATTR);

  // instantiate object, adding properties that apply to the whole dropdown
  const product = {
    // DOM references
    wrap: $wrap[0],
    wrapBlockClass,
    sizeEl,
    // product info
    regularPrice,
    chosen: {},
    // useful booleans
    oneSize: options && options.length === 1,
    allInStock: options ? !any(options, hasAttr, 'data-oos') : !hasAttr(chosen, 'data-oos'),
    allPreorder: options ? all(options, hasAttr, 'data-preorder') : hasAttr(chosen, 'data-preorder'),
    allOnSale: options ? all(options, isOnSale, regularPrice) : isOnSale(chosen, regularPrice),
  };
  updateChosenData(product, chosen);

  return product;
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/lib/create-product-data.js